import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap } from 'rxjs';

import { LinkRegistryService } from '../../services/link-registry.service';
import { IncludeService } from './include.service';
import type { SafeHtml } from '@angular/platform-browser';

/**
 * Include Component
 *
 * Render static HTML content and images from input URL.
 *
 * @example
 * <td-wb-include linkId="http://localhost:4200/test.html">
 * </td-wb-include>
 *
 */
@Component({
  template: '<div [innerHTML]="html()"></div>',
  selector: 'td-wb-include',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IncludeComponent {
  /**
   * The url that will be included
   */
  linkId: InputSignal<string | undefined> = input<string>();
  /**
   * The boolean indicates to add default width and height or not
   */
  addDefaultWidthAndHeight: InputSignal<boolean | undefined> = input<boolean>();
  /**
   * The customized image height for any image in the url
   */
  imageHeight: InputSignal<string | undefined> = input<string>();
  /**
   * The customized image width for any image in the url
   */
  imageWidth: InputSignal<string | undefined> = input<string>();

  private readonly linkRegistryService: LinkRegistryService = inject(LinkRegistryService);
  private readonly includeService: IncludeService = inject(IncludeService);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

  // Synchronous derivation of parameters
  private readonly requestParams: Signal<{ url: string; baseUrl: string } | undefined> = computed(() => {
    const id = this.linkId();
    if (!id) {
      return undefined;
    }

    let url = this.linkRegistryService.getLink(id, undefined, true);
    if (url === undefined) {
      url = id;
    }

    const baseUrl = url.includes('/') ? url.substring(0, url.lastIndexOf('/')) : '';
    return { url, baseUrl };
  });

  // Asynchronous HTML fetching and processing
  html: Signal<SafeHtml | undefined> = toSignal(
    toObservable(this.requestParams).pipe(
      switchMap((params: { url: string; baseUrl: string } | undefined) => {
        if (!params) {
          return of(undefined);
        }

        return this.includeService.getHtml(params.url).pipe(
          map((htmlText: string): SafeHtml | undefined => {
            if (htmlText.trim().length > 0) {
              const processedHtml = this.includeService.resolveForeignImgTags(
                htmlText,
                params.baseUrl,
                this.addDefaultWidthAndHeight(),
                this.imageHeight(),
                this.imageWidth()
              );
              // Use bypassSecurityTrustHtml to avoid double sanitization
              return this.sanitizer.bypassSecurityTrustHtml(processedHtml);
            }
            return undefined;
          }),
          // Resilience: ensure the stream doesn't die on failure
          catchError(() => of(undefined))
        );
      })
    ),
    { initialValue: undefined }
  );
}
